import { useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { TurnstileWidget, turnstileEnabled } from '@/lib/turnstile'
import { appCopy } from '../copy'

/** Company self-registration (MULTI_COMPANY_TENANCY §4): creates the
 * company and its OWNER account, then lands in the email-verification gate.
 * Early access is invite-only: only a local dev build renders the form. The
 * staging build-time flag (`VITE_PUBLIC_REGISTRATION`) that used to open it was
 * retired 2026-09-13 (FF-AUDIT-086, owner decision) — new companies are created
 * by a platform admin (DEPLOYMENT_GUIDE §2b Path C). The API keeps its own
 * runtime gate (`PUBLIC_REGISTRATION`) for local and CI stacks. */
export const registrationOpen = import.meta.env.DEV

export function RegisterPage() {
  if (!registrationOpen) return <InviteOnlyNotice />
  return <RegisterForm />
}

function InviteOnlyNotice() {
  const copy = useCopy(appCopy)
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-center">
        <h1 className="text-xl font-semibold">{copy.shell.brand}</h1>
        <p className="text-sm text-muted-foreground">
          {copy.registration.inviteOnly}
        </p>
        <Link
          to="/login"
          className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {copy.authCommon.goToSignIn}
        </Link>
      </div>
    </main>
  )
}

function RegisterForm() {
  const copy = useCopy(appCopy)
  const { registerCompany } = useAuth()
  const navigate = useNavigate()
  const [companyName, setCompanyName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined)
  const [turnstileEpoch, setTurnstileEpoch] = useState(0)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (password !== confirm) {
      setError(copy.registration.mismatch)
      return
    }
    if (password.length < 12) {
      setError(copy.registration.tooShort)
      return
    }
    setBusy(true)
    try {
      await registerCompany({
        companyName,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        owner: { firstName, lastName, email, password },
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.registration.failed)
      setTurnstileToken(undefined)
      setTurnstileEpoch((epoch) => epoch + 1)
    } finally {
      setBusy(false)
    }
  }

  const field = 'w-full rounded-md border bg-background px-3 py-2'

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <form onSubmit={(e) => void onSubmit(e)} className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6">
        <div>
          <h1 className="text-xl font-semibold">{copy.shell.brand}</h1>
          <p className="text-sm text-muted-foreground">{copy.registration.prompt}</p>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.registration.companyName}</span>
          <input required minLength={2} maxLength={200} value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={field} autoComplete="organization" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{copy.registration.firstName}</span>
            <input required maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} autoComplete="given-name" />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{copy.registration.lastName}</span>
            <input required maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} autoComplete="family-name" />
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.registration.workEmail}</span>
          <input type="email" required maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} className={field} autoComplete="email" />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.authCommon.password}</span>
          <input type="password" required minLength={12} maxLength={200} value={password} onChange={(e) => setPassword(e.target.value)} className={field} autoComplete="new-password" />
          <span className="mt-1 block text-xs text-muted-foreground">
            {copy.registration.passwordHint}
          </span>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.registration.confirmPassword}</span>
          <input type="password" required minLength={12} maxLength={200} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} autoComplete="new-password" />
        </label>

        <TurnstileWidget key={turnstileEpoch} onToken={setTurnstileToken} />

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button className="w-full"
 type="submit"
 disabled={busy || (turnstileEnabled && !turnstileToken)}

 >
          {busy ? copy.registration.creating : copy.registration.createCompany}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {copy.registration.existingAccount}{' '}
          {/* `underline`, not `hover:underline`: inside a block of text, colour
              alone does not mark this as a link for anyone who cannot
              distinguish the hue (axe `link-in-text-block`, serious). Standalone
              links elsewhere are fine on hover-underline — it is specifically
              links surrounded by prose that need the permanent affordance. */}
          <Link to="/login" className="text-primary underline">
            {copy.registration.signIn}
          </Link>
        </p>
      </form>
    </main>
  )
}
